// src/components/BathroomWall.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import '../styles/BathroomWall.css';
import VoteStepper from './VoteStepper';
import { rescueEmoji } from '../utils/emojiRescue';

const DEFAULT_BACKEND = "https://partybutton-private-a027fa6fce78.herokuapp.com";

const MAX_WALL = 10;

// Small but distinct palette — extend anytime
const USER_EMOJI_PALETTE = [
  '🐶','🐱','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵',
  '🐔','🐧','🐦','🦆','🦉','🦄','🐝','🦋','🐞','🦖','🐙','🦑',
  '🌸','🌼','🌿','🍀','🍄','🌈','⭐','⚡','🔥','❄️','💧','🌪️',
  '🎈','🎲','🎧','🎵','🎯','🛼','🪩','🎨','🧩','♟️','🧿','🔮',
  '😀','😎','🤠','🤡','😺','😼','🙃','😉','😇','🥳','🤖','👾'
];

function djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function pickEmojiForKey(key) {
  const idx = djb2(key) % USER_EMOJI_PALETTE.length;
  return USER_EMOJI_PALETTE[idx];
}

function cleanWallEmoji(value, fallbackKey = '') {
  const s = String(value || '').trim();

  // Bad legacy/corrupted emoji stored as � or boxed ?
  if (!s || s.includes('\uFFFD') || s === '?') {
    return pickEmojiForKey(fallbackKey || 'fallback');
  }

  return s;
}

function fromCommentDoc(doc) {
  if (!doc) return null;
  const rawId = doc._id ?? doc.id;
  if (!rawId) return null;

  const score = Number(doc.score || 0);
  const upvotes = score > 0 ? score : 0;
  const strikes = score < 0 ? -score : 0;

  const authorKey = String(doc.authorKey || rawId || 'fallback');
  const userEmoji = cleanWallEmoji(doc.avatar, authorKey);

  return {
    id: String(rawId),
    text: doc.text,
    userEmoji,
    upvotes,
    strikes,
    authorKey,
    isMine: doc.isMine === true,
    netScore: score,
    ts: doc.updatedAt || doc.createdAt || Date.now()
  };
}

export default function BathroomWall({
  backendBase,
  placeId,
  posterLoc,
  me,
  mode = 'venue',
}) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({}); // { [id]: true } during vote actions
  const [wsConnected, setWsConnected] = useState(false);
  const [myAuthorKey, setMyAuthorKey] = useState('');

  const isRestroomMode = mode === 'restroom';
  const wallPlaceId = isRestroomMode && placeId ? `restroom:${placeId}` : placeId;

  const [notice, setNotice] = useState(null);

  // UGC compliance: local block list (per device)
  const [blockedAuthors, setBlockedAuthors] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('WALL_BLOCKED_AUTHORS') || '[]') || [];
    } catch {
      return [];
    }
  });

  const [showRules, setShowRules] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showBlocks, setShowBlocks] = useState(false);

  const pollTimerRef = useRef(null);
  const wallRef = useRef(null);
    
    useEffect(() => {
    try {
      localStorage.setItem('WALL_BLOCKED_AUTHORS', JSON.stringify(blockedAuthors));
    } catch {}
  }, [blockedAuthors]);

  const visibleMessages = useMemo(() => {
    if (!blockedAuthors?.length) return messages;
    const blocked = new Set(blockedAuthors);
    return messages.filter((m) => !blocked.has(m.authorKey));
  }, [messages, blockedAuthors]);

    useEffect(() => {
      const run = () => rescueEmoji(wallRef.current);
      run();
      const raf = requestAnimationFrame(run);
      const timer = setTimeout(run, 120);

      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(timer);
      };
    }, [visibleMessages]);
    
    
  // 👤 Stable per-device identity (uid + emoji)
  const { myUid, myEmoji } = useMemo(() => {
    const uidKey = 'WALL_UID';
    const emoKey = 'WALL_EMOJI';

    let uid = localStorage.getItem(uidKey);
    let emoji = localStorage.getItem(emoKey);

    if (!uid) {
      uid =
        (crypto?.randomUUID && crypto.randomUUID()) ||
        `uid_${Math.random().toString(16).slice(2)}_${Date.now()}`;
      localStorage.setItem(uidKey, uid);
    }

      emoji = cleanWallEmoji(emoji, uid);

      if (!emoji || emoji === '?' || emoji.includes('\uFFFD')) {
        emoji = pickEmojiForKey(uid);
      }

      localStorage.setItem(emoKey, emoji);

    return { myUid: uid, myEmoji: emoji };
  }, []);

  const canPost = useMemo(() => {
    const t = String(text || '').trim();
    return t.length > 0 && t.length <= 500;
  }, [text]);

  const load = useCallback(async () => {
    if (!wallPlaceId) return;
    try {
      const base = backendBase || DEFAULT_BACKEND;
      const r = await fetch(`${base}/api/comments/${encodeURIComponent(wallPlaceId)}`, {
        cache: 'no-store',
        headers: {
          'X-Party-Uid': myUid, // IMPORTANT: avoid shared-IP rate-limit collisions
        },
      });

      // If we’re being rate-limited, don’t scare the user; keep last-good wall.
      if (r.status === 429) return;

      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }

      const d = await r.json();
      const comments = Array.isArray(d?.comments) ? d.comments : [];
      const msgs = comments.map(fromCommentDoc).filter(Boolean);
      setMyAuthorKey(String(d?.viewerAuthorKey || ''));

      // server already sorts newest first; keep MAX_WALL
      setMessages(msgs.slice(0, MAX_WALL));
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load wall.');
    }
  }, [backendBase, wallPlaceId, myUid]);

  // initial load
  useEffect(() => {
    load();
  }, [load]);

  // 🔄 Backstop polling: only when WS is down, and slower than before (cost + rate-limit safe)
  useEffect(() => {
    if (!wallPlaceId) return;

    // If WS is alive, don’t poll.
    if (wsConnected) return;

    let stopped = false;

    const schedule = () => {
      if (stopped) return;

      // 10–12s jitter so multiple devices don’t sync-spike
      const delay = 10000 + Math.floor(Math.random() * 2000);
      pollTimerRef.current = setTimeout(async () => {
        await load();
        schedule();
      }, delay);
    };

    schedule();

    return () => {
      stopped = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [wallPlaceId, load, wsConnected]);

  // Realtime WS: new posts / deletes / votes (server already sends these)
  useEffect(() => {
    if (!wallPlaceId) return;

    const origin =
      backendBase && /^https?:/i.test(backendBase)
        ? backendBase
        : DEFAULT_BACKEND;

    const wsUrl = origin.replace(/^http/i, 'ws');

    let ws;
    let retryTimer;
    let pingTimer;
    let closed = false;

    const connect = () => {
      if (closed) return;

      try {
        ws = new WebSocket(`${wsUrl}/ws`);

        ws.onopen = () => {
          setWsConnected(true);

          // keepalive ping (some proxies close idle sockets)
          pingTimer = setInterval(() => {
            try {
              ws.send(JSON.stringify({ type: 'ping' }));
            } catch {}
          }, 15000);
        };

        ws.onclose = () => {
          setWsConnected(false);
          if (pingTimer) clearInterval(pingTimer);
          pingTimer = null;

          // reconnect with backoff
          if (!closed) {
            retryTimer = setTimeout(connect, 1500);
          }
        };

        ws.onerror = () => {
          // let onclose handle reconnect
        };

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (!msg) return;
             
              if (msg.type === 'wall-post' && msg.placeId === wallPlaceId) {
                const messageAuthorKey = String(
                  msg.message?.authorKey || msg.message?.id || 'fallback'
                );

                const m = {
                  ...msg.message,
                  authorKey: messageAuthorKey,
                  isMine: Boolean(
                    myAuthorKey && messageAuthorKey === myAuthorKey
                  ),
                  userEmoji: cleanWallEmoji(
                    msg.message?.userEmoji || msg.message?.avatar,
                    messageAuthorKey
                  ),
                };

                if (!m?.id) return;
                  
                
              setMessages((prev) => {
                // de-dupe
                const without = prev.filter((x) => x.id !== m.id);
                const next = [m, ...without];
                return next.slice(0, MAX_WALL);
              });
            }

              // DELETE (server sends messageId, client was reading id)
              if (msg.type === 'wall-delete' && msg.placeId === wallPlaceId) {
                const id = String(msg.messageId || msg.id || '');
                if (!id) return;
                setMessages((prev) => prev.filter((x) => x.id !== id));
              }

              // VOTE (server sends type 'comment-vote' with msg.comment; client was listening for 'wall-vote')
              if ((msg.type === 'wall-vote' || msg.type === 'comment-vote') && msg.placeId === wallPlaceId) {
                const payload = msg.comment || msg.message || msg;
                const id = String(payload.id || msg.id || msg.messageId || '');
                if (!id) return;

                const score = Number(payload.score ?? msg.score ?? 0);

                setMessages((prev) =>
                  prev.map((x) => {
                    if (x.id !== id) return x;
                    const upvotes = score > 0 ? score : 0;
                    const strikes = score < 0 ? -score : 0;
                    return {
                      ...x,
                      authorKey: String(payload.authorKey || x.authorKey || id),
                      netScore: score,
                      upvotes,
                      strikes,
                    };
                  })
                );
              }

          } catch {
            // ignore
          }
        };
      } catch {
        // If WS fails hard, rely on polling
        setWsConnected(false);
      }
    };

    connect();

    return () => {
      closed = true;
      setWsConnected(false);
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      try {
        ws?.close();
      } catch {}
    };
  }, [backendBase, wallPlaceId, myAuthorKey]);

  // ---- post ----
  const post = useCallback(async () => {
    if (!canPost || posting) return;

    setPosting(true);
    setError(null);

    try {
      const base = backendBase || DEFAULT_BACKEND;
      const r = await fetch(`${base}/api/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Party-Uid': myUid,
        },
        body: JSON.stringify({
          placeId: wallPlaceId,
          text: String(text).trim(),
          avatar: myEmoji,
        }),
      });

      if (r.status === 429) {
        throw new Error('Slow down (rate limited).');
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.error || `HTTP ${r.status}`);
      }

      await r.json().catch(() => ({}));
      setText('');

      // Let the server / WebSocket be the source of truth.
      // This avoids the brief double-render.
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to post.');
    } finally {
      setPosting(false);
    }
  }, [canPost, text, backendBase, wallPlaceId, myEmoji, myUid, load, posting]);

  // ---- votes (local + server PATCH to /api/comments/:id/vote) ----
  const vote = useCallback(
    async (messageId, delta) => {
      if (!wallPlaceId || busy[messageId]) return;
      setBusy((s) => ({ ...s, [messageId]: true }));

      try {
        const base = backendBase || DEFAULT_BACKEND;
        const r = await fetch(`${base}/api/comments/${messageId}/vote`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Party-Uid': myUid,
          },
          body: JSON.stringify({ delta }),
        });

        if (r.status === 429) {
          throw new Error('Slow down (rate limited).');
        }

        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }

        const d = await r.json().catch(() => ({}));

          const updatedComment = d?.comment || d?.message || d;

          if (d?.deleted && (d.id || d.messageId)) {
            const deletedId = String(d.id || d.messageId);
            setMessages((prev) => prev.filter((m) => m.id !== deletedId));
          } else if (updatedComment?._id || updatedComment?.id) {
            const updatedId = String(updatedComment._id || updatedComment.id);
            const score = Number(updatedComment.score ?? 0);

            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== updatedId) return m;
                const upvotes = score > 0 ? score : 0;
                const strikes = score < 0 ? -score : 0;
                return { ...m, netScore: score, upvotes, strikes };
              })
            );
          }
      } catch (e) {
        setError(e?.message || 'Vote failed.');
      } finally {
        setBusy((s) => ({ ...s, [messageId]: false }));
      }
    },
    [backendBase, wallPlaceId, busy, myUid]
  );

  // ---- report + block (UGC compliance) ----
  const reportComment = useCallback(
    async (commentId, commentText) => {
      if (!commentId) return;

      const reason = window.prompt(
        'Report this comment? (Optional: tell us why)'
      );

      // user hit cancel
      if (reason === null) return;

      try {
        const base = backendBase || DEFAULT_BACKEND;
        const r = await fetch(`${base}/api/comments/${commentId}/report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Party-Uid': myUid,
          },
          body: JSON.stringify({
            placeId: wallPlaceId,
            reason: String(reason || '').slice(0, 500),
            text: String(commentText || '').slice(0, 500),
          }),
        });

        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }

        setNotice('Reported — thank you.');
        setTimeout(() => setNotice(null), 2500);
      } catch (e) {
        setError(e?.message || 'Failed to report.');
      }
    },
    [backendBase, myUid, wallPlaceId]
  );

  const blockAuthor = useCallback((authorKey) => {
    if (!authorKey) return;

    const ok = window.confirm(
      'Block this user? You won’t see comments from them on this device.'
    );
    if (!ok) return;

    setBlockedAuthors((prev) => {
      const next = new Set(prev || []);
      next.add(String(authorKey));
      return Array.from(next);
    });

    setNotice('User blocked.');
    setTimeout(() => setNotice(null), 2000);
  }, []);

  const unblockAuthor = useCallback((authorKey) => {
    setBlockedAuthors((prev) => (prev || []).filter((k) => k !== authorKey));
  }, []);

  const unblockAll = useCallback(() => {
    setBlockedAuthors([]);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      post();
    }
  };

  const timeOf = (t) => {
    try {
      const d = typeof t === 'number' ? new Date(t) : new Date(String(t));
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const wallVenueName = useMemo(() => {
    // posterLoc is passed from VenueCard; if it ever changes shape, this still won't crash.
    const n = posterLoc && typeof posterLoc === 'object' ? posterLoc.name : '';
    return String(n || '').trim();
  }, [posterLoc]);

  const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: 10 };
  const leftStyle = { flex: '0 0 auto', width: 34, marginTop: 1 };
  const midStyle = { flex: '1 1 auto', minWidth: 0 };
  const txtStyle = { display: 'inline', fontSize: 15, lineHeight: '22px', verticalAlign: 'middle', wordBreak: 'break-word' };
  const rightStyle = { flex: '0 0 auto', textAlign: 'right', minWidth: 86 };

  return (
          <div className="bathroom-wall" ref={wallRef}>
      <div className="wall-header">
        <h3>{isRestroomMode ? '🚻 Potty Report' : 'Wall Scrawl'}</h3>
        {isRestroomMode ? (
          <p className="potty-report-help">
            Leave a quick access or condition update for the next person.
          </p>
        ) : null}

          <div
            className="wall-header-actions"
            style={{
              display: "flex",
              gap: 10,
              marginTop: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              className="wall-link"
              onClick={() => setShowRules(true)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: 13,
                opacity: 0.85,
              }}
            >
              Rules
            </button>
          
          <button
            type="button"
            className="wall-link"
            onClick={() => setShowContact(true)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: 13,
              opacity: 0.85,
            }}
          >
            Contact
          </button>

            {blockedAuthors?.length ? (
              <button
                type="button"
                className="wall-link"
                onClick={() => setShowBlocks(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: 13,
                  opacity: 0.85,
                }}
              >
                Blocked ({blockedAuthors.length})
              </button>
            ) : null}
          </div>

          {notice ? (
            <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
              {notice}
            </div>
          ) : null}
        </div> {/* ✅ this closes .wall-header properly */}

      <div className="wall-input">
        <input
          type="text"
          value={text}
          placeholder={
            isRestroomMode
              ? 'Open, locked, clean, code needed…'
              : 'Scrawl on the wall…'
          }
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!wallPlaceId || posting}
        />
        <button
          onClick={post}
          disabled={!wallPlaceId || posting || !canPost}
        >
          {posting ? 'Posting…' : 'Post'}
        </button>
      </div>

      {error ? (
        <div className="wall-error" style={{ marginTop: 8 }}>
          {error}
        </div>
      ) : null}

      <div className="wall-messages">
        {visibleMessages.map((m) => {
          const net = Number(m.netScore ?? (m.upvotes - m.strikes) ?? 0);

          // Keep the stepper non-negative while showing a separate strike status.
          // The server removes a comment only after two net strikes.
          const displayScore = Math.max(0, net);
          const strikeCount = Math.max(0, -net);
          const isMine = Boolean(
            m.isMine ||
            (myAuthorKey && m.authorKey === myAuthorKey)
          );

          return (
            <div key={m.id} className="wall-row" style={rowStyle}>
              {/* LEFT: avatar */}
              <div className="left" style={leftStyle}>
                  <span className="avatar emoji">
                    {cleanWallEmoji(m.userEmoji, m.authorKey)}
                  </span>
              </div>

              {/* MIDDLE: text on SAME LINE as the emoji */}
              <div className="mid" style={midStyle}>
                <div className="txt" style={txtStyle}>{m.text}</div>
              </div>

              {/* RIGHT: buttons (top-right), timestamp below */}
              <div className="right" style={rightStyle}>
                <div className={`vote-row ${net < 0 ? "struck" : ""}`}>
                  <VoteStepper
                    value={displayScore}
                    min={0}
                    onChange={(_, delta) => {
                      vote(m.id, delta);
                    }}
                    isBusy={!!busy[m.id]}
                    size="sm"
                  />
                </div>

                {strikeCount > 0 ? (
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 11,
                      fontWeight: 800,
                      opacity: 0.72,
                    }}
                  >
                    {strikeCount} of 2 strikes
                  </div>
                ) : null}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => reportComment(m.id, m.text)}
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, opacity: 0.75, textDecoration: 'underline' }}
                  >
                    Report
                  </button>

                  {!isMine ? (
                    <button
                      type="button"
                      onClick={() => blockAuthor(m.authorKey)}
                      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, opacity: 0.75, textDecoration: 'underline' }}
                    >
                      Block
                    </button>
                  ) : null}
                </div>

                <div
                  className="time"
                  style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
                >
                  {timeOf(m.ts ?? m.time)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ---- Rules / Contact / Claim / Blocked modals ---- */}
      {(showRules || showContact || showBlocks) ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowRules(false);
            setShowContact(false);
            setShowBlocks(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(520px, 100%)',
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {showRules
                  ? 'Community Rules'
                  : showContact
                  ? 'Contact'
                  : 'Blocked Users'}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowRules(false);
                  setShowContact(false);
                  setShowBlocks(false);
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: '16px' }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {showRules ? (
              <div style={{ marginTop: 12, fontSize: 14, lineHeight: '20px' }}>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  <li>No hate speech, slurs, or harassment.</li>
                  <li>No threats, doxxing, or personal info.</li>
                  <li>No sexual content involving minors (instant ban &amp; report).</li>
                  <li>Keep it fun — you can always downvote bad vibes.</li>
                </ul>
                          <div style={{ marginTop: 10, opacity: 0.8 }}>
                            See something off? Tap <b>Report</b>. We aim to review reports within 24 hours. Want peace? Tap <b>Block</b> to hide that user’s posts on this device.
                          </div>

              </div>
            ) : null}

            {showContact ? (
              <div style={{ marginTop: 12, fontSize: 14, lineHeight: '20px' }}>
                <div style={{ marginBottom: 10, opacity: 0.9 }}>
                  For moderation issues, feature requests, or bugs:
                </div>

                <a
                  href="mailto:support@the-party-button.com?subject=Party%20Button%20Wall%20Report"
                  style={{ textDecoration: 'underline' }}
                >
                  support@the-party-button.com
                </a>

                
              </div>
            ) : null}

                {showBlocks ? (
              <div style={{ marginTop: 12, fontSize: 14, lineHeight: '20px' }}>
                {blockedAuthors?.length ? (
                  <>
                    <div style={{ marginBottom: 10, opacity: 0.9 }}>
                      You’re currently blocking {blockedAuthors.length} user{blockedAuthors.length === 1 ? '' : 's'} on this device:
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {blockedAuthors.map((k, index) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => unblockAuthor(k)}
                          style={{
                            border: '1px solid rgba(0,0,0,0.2)',
                            borderRadius: 999,
                            padding: '6px 10px',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 13,
                          }}
                          title="Unblock"
                        >
                          Blocked user {index + 1} ✕
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const ok = window.confirm('Unblock everyone?');
                        if (ok) unblockAll();
                      }}
                      style={{
                        marginTop: 12,
                        border: '1px solid rgba(0,0,0,0.25)',
                        borderRadius: 10,
                        padding: '8px 10px',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      Unblock all
                    </button>
                  </>
                ) : (
                  <div style={{ opacity: 0.85 }}>No one is blocked right now.</div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

    </div>
  );
}
